import { skipToken, useQuery } from '@tanstack/react-query';
import { z } from 'zod';
import { TRANSCRIPTION_SERVICE_TRANSCRIPTS_QUERY_KEY } from '../contracts';
import { getSessionDataConfig } from './config';
import { DiarisationStatusSchema, DiarisedTranscriptSchema } from './diarisationSchemas';
import {
  isTerminalDiarisation,
  orderTranscriptChunks,
  updateTranscriptCache,
  type TranscriptSnapshot,
} from './transcriptCache';

export const ChunkResponseSchema = z.object({
  state: z.enum(['staged', 'persisted']),
  session_id: z.string(),
  recording_id: z.string(),
  delivery_id: z.string(),
  chunk_index: z.number().int().min(0).max(5000),
  revision: z.string().datetime({ offset: true }),
  start_offset_ms: z.number().nonnegative(),
  duration_ms: z.number().nonnegative(),
  size_bytes: z.number().nonnegative(),
  kind: z.enum(['transcribed', 'failed']),
  text: z.string(),
  confidence: z.number().nullable(),
  model: z.string().nullable(),
  diarised_transcript: DiarisedTranscriptSchema.nullish(),
  diarisation_status: DiarisationStatusSchema.nullish(),
});
export type ChunkResponse = z.infer<typeof ChunkResponseSchema>;

export const DeliveryProgressSchema = ChunkResponseSchema.pick({
  session_id: true,
  recording_id: true,
  delivery_id: true,
}).extend({
  last_chunk_index: z.number().int().min(0).max(5000).nullable(),
  committed_indices: z.array(z.number().int().min(0).max(5000)).max(5001),
});

type ChunkResponsePreview = {
  startedAt: number;
  response: ChunkResponse;
};
const PREVIEW_QUERY_KEY = 'chunkResponsePreviews';
const EMPTY_PREVIEWS: ChunkResponsePreview[] = [];

export function isChunkResponsePreviewQuery(
  queryKey: readonly unknown[],
  userId: string,
  sessionId: string
): boolean {
  return (
    queryKey[0] === PREVIEW_QUERY_KEY &&
    queryKey[1] === userId &&
    queryKey[2] === sessionId
  );
}

export function recordChunkResponsePreview(
  userId: string,
  response: ChunkResponse,
  startedAt: number
): void {
  if (response.state !== 'staged') throw new Error('Expected staged chunk');
  const client = getSessionDataConfig().getQueryClient();
  const persisted = client.getQueryData<TranscriptSnapshot>([
    TRANSCRIPTION_SERVICE_TRANSCRIPTS_QUERY_KEY,
    response.session_id,
  ]);
  if (
    persisted?.recordings.some(
      (recording) =>
        recording.recording_id === response.recording_id &&
        recording.chunks.some((chunk) => chunk.timestamp_ms === response.start_offset_ms)
    )
  )
    return;
  client.setQueryData<ChunkResponsePreview[]>(
    [PREVIEW_QUERY_KEY, userId, response.session_id],
    (current = []) => {
      const index = current.findIndex(
        (item) =>
          item.response.recording_id === response.recording_id &&
          item.response.delivery_id === response.delivery_id &&
          item.response.chunk_index === response.chunk_index
      );
      const previous = current[index];
      if (
        previous &&
        Date.parse(previous.response.revision) >= Date.parse(response.revision)
      )
        return current;
      const next = [...current];
      next[index < 0 ? next.length : index] = { response, startedAt };
      return next;
    }
  );
}

export function recordPersistedChunkResponse(
  userId: string,
  response: ChunkResponse,
  startedAt: number
): void {
  if (response.state !== 'persisted') throw new Error('Expected persisted chunk');
  const client = getSessionDataConfig().getQueryClient();
  void client.cancelQueries({
    queryKey: [TRANSCRIPTION_SERVICE_TRANSCRIPTS_QUERY_KEY, response.session_id],
  });
  if (response.kind === 'transcribed') {
    updateTranscriptCache<TranscriptSnapshot>(
      response.session_id,
      (snapshot) => mergeChunkResponses(snapshot, [{ response, startedAt }]),
      { session_id: response.session_id, recordings: [] }
    );
  }
  client.setQueryData<ChunkResponsePreview[]>(
    [PREVIEW_QUERY_KEY, userId, response.session_id],
    (current) =>
      current?.filter(
        (item) =>
          !(
            item.response.recording_id === response.recording_id &&
            item.response.delivery_id === response.delivery_id &&
            item.response.chunk_index === response.chunk_index
          )
      )
  );
}

export function clearChunkResponsePreviews(userId: string, sessionId?: string): void {
  getSessionDataConfig()
    .getQueryClient()
    .setQueriesData(
      {
        queryKey:
          sessionId === undefined
            ? [PREVIEW_QUERY_KEY, userId]
            : [PREVIEW_QUERY_KEY, userId, sessionId],
      },
      EMPTY_PREVIEWS
    );
}

export function useChunkResponsePreviews(
  userId: string | undefined,
  sessionId: string
): ChunkResponsePreview[] {
  const { data } = useQuery<ChunkResponsePreview[]>({
    queryKey: [PREVIEW_QUERY_KEY, userId, sessionId],
    queryFn: skipToken,
  });
  return data ?? EMPTY_PREVIEWS;
}

/** Display projection only: never write this result into a transcript query or mutation. */
export function mergeChunkResponsePreviews<T extends TranscriptSnapshot>(
  snapshot: T,
  previews: readonly ChunkResponsePreview[]
): T {
  return mergeChunkResponses(snapshot, previews);
}

function mergeChunkResponses<T extends TranscriptSnapshot>(
  snapshot: T,
  previews: readonly ChunkResponsePreview[]
): T {
  const additions = new Map<string, ChunkResponsePreview>();
  for (const preview of previews) {
    const { response } = preview;
    if (response.session_id !== snapshot.session_id || response.kind !== 'transcribed')
      continue;
    const recording = snapshot.recordings.find(
      (item) => item.recording_id === response.recording_id
    );
    const existing = recording?.chunks.find(
      (chunk) => chunk.timestamp_ms === response.start_offset_ms
    );
    if (
      existing &&
      !(
        response.state === 'persisted' &&
        !isTerminalDiarisation(existing.diarisation_status) &&
        isTerminalDiarisation(response.diarisation_status) &&
        Date.parse(response.revision) >= Date.parse(existing.created_at)
      )
    )
      continue;
    const key = JSON.stringify([response.recording_id, response.start_offset_ms]);
    const previous = additions.get(key);
    if (
      !previous ||
      Date.parse(response.revision) > Date.parse(previous.response.revision)
    )
      additions.set(key, preview);
  }
  if (!additions.size) return snapshot;
  const recordings = snapshot.recordings.map((recording) => ({
    ...recording,
    chunks: [...recording.chunks],
  }));
  for (const { response, startedAt } of additions.values()) {
    let recording = recordings.find(
      (item) => item.recording_id === response.recording_id
    );
    if (!recording) {
      recording = {
        recording_id: response.recording_id,
        start_date: new Date(startedAt).toISOString(),
        chunks: [],
        issues: [],
      };
      recordings.push(recording);
    }
    const existingIndex = recording.chunks.findIndex(
      (chunk) => chunk.timestamp_ms === response.start_offset_ms
    );
    const existing = recording.chunks[existingIndex];
    if (existing) {
      recording.chunks[existingIndex] = {
        ...existing,
        created_at: response.revision,
        diarised_transcript: response.diarised_transcript,
        diarisation_status: response.diarisation_status,
      };
      continue;
    }
    recording.chunks.push({
      ...(response.diarised_transcript !== undefined
        ? { diarised_transcript: response.diarised_transcript }
        : {}),
      ...(response.diarisation_status !== undefined
        ? { diarisation_status: response.diarisation_status }
        : {}),
      index: response.chunk_index,
      text: response.text,
      confidence: response.confidence ?? 0,
      model: response.model ?? '',
      audio_info: {
        duration: response.duration_ms,
        size: response.size_bytes,
        mime_type: null,
      },
      created_at: response.revision,
      timestamp_ms: response.start_offset_ms,
      timestamp_cumulative_ms: response.start_offset_ms,
    });
  }
  return orderTranscriptChunks({ ...snapshot, recordings });
}
